import { 
  doc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove,
  increment,
  getDoc,
  writeBatch,
  collection,
  query,
  getDocs
} from '@firebase/firestore';
import { Article, ReactionType } from '../types/firebase';
import { db } from '../config/firebase';

// Initialize empty reactions object with all possible reaction types
export const initializeArticleReactions = () => ({
  thumbsUp: { count: 0, users: [] },
  thumbsDown: { count: 0, users: [] },
  love: { count: 0, users: [] },
  laugh: { count: 0, users: [] },
  wow: { count: 0, users: [] },
  cry: { count: 0, users: [] },
  vomit: { count: 0, users: [] },
  angry: { count: 0, users: [] },
  poop: { count: 0, users: [] },
  clown: { count: 0, users: [] }
});

export const addArticleReaction = async (
  articleId: string,
  userId: string,
  reactionType: ReactionType
) => {
  const articleRef = doc(db, 'articles', articleId);
  const batch = writeBatch(db);

  // First, remove user from ALL reaction types to ensure one reaction per user
  const articleDoc = await getDoc(articleRef);
  if (!articleDoc.exists()) {
    throw new Error('Article not found');
  }

  const article = articleDoc.data() as Article;
  Object.entries(article.reactions || {}).forEach(([key, value]) => {
    if (value?.users?.includes(userId)) {
      batch.update(articleRef, {
        [`reactions.${key}.count`]: increment(-1),
        [`reactions.${key}.users`]: arrayRemove(userId)
      });
    }
  });

  // Then add the new reaction
  batch.update(articleRef, {
    [`reactions.${reactionType}.count`]: increment(1),
    [`reactions.${reactionType}.users`]: arrayUnion(userId)
  });

  await batch.commit();
};

export const removeArticleReaction = async (
  articleId: string,
  userId: string,
  reactionType: ReactionType
) => {
  const articleRef = doc(db, 'articles', articleId);
  await updateDoc(articleRef, {
    [`reactions.${reactionType}.count`]: increment(-1),
    [`reactions.${reactionType}.users`]: arrayRemove(userId)
  });
};

// Function to completely reset an article's reactions
export const resetArticleReactions = async (articleId: string): Promise<void> => {
  const articleRef = doc(db, 'articles', articleId);
  const articleDoc = await getDoc(articleRef);

  if (!articleDoc.exists()) {
    throw new Error('Article not found');
  }

  // Reset reactions to initial state
  await updateDoc(articleRef, {
    reactions: initializeArticleReactions()
  });
};

export const cleanupArticleReactions = async (articleId: string): Promise<void> => {
  const articleRef = doc(db, 'articles', articleId);
  const articleDoc = await getDoc(articleRef);

  if (!articleDoc.exists()) {
    throw new Error('Article not found');
  }

  // First, completely reset the reactions
  await resetArticleReactions(articleId);

  const freshDoc = await getDoc(articleRef);
  const article = freshDoc.data() as Article;

  // Initialize if needed
  if (!article.reactions) {
    article.reactions = initializeArticleReactions();
    await updateDoc(articleRef, { reactions: article.reactions });
    return;
  }
};

// Function to clean up all articles' reactions
export const cleanupAllArticleReactions = async (): Promise<void> => {
  const articlesRef = collection(db, 'articles');
  const articlesSnapshot = await getDocs(query(articlesRef));

  for (const doc of articlesSnapshot.docs) {
    try {
      await cleanupArticleReactions(doc.id);
    } catch (error) {
      console.error(`Error cleaning up reactions for article ${doc.id}:`, error);
    }
  }
};

export const getArticleReactionCounts = async (articleId: string): Promise<{ [key in ReactionType]: number }> => {
  const articleRef = doc(db, 'articles', articleId);
  const articleDoc = await getDoc(articleRef);

  if (!articleDoc.exists()) {
    throw new Error('Article not found');
  }

  const article = articleDoc.data() as Article;
  
  // Initialize reactions if they don't exist
  if (!article.reactions) {
    article.reactions = initializeArticleReactions();
    await updateDoc(articleRef, { reactions: article.reactions });
  }

  // Return counts, defaulting to 0 for any missing reactions
  return Object.keys(initializeArticleReactions()).reduce((acc, key) => {
    acc[key as ReactionType] = article.reactions[key as ReactionType]?.count || 0;
    return acc;
  }, {} as { [key in ReactionType]: number });
};

export const getUserArticleReactions = async (articleId: string, userId: string): Promise<ReactionType[]> => {
  const articleRef = doc(db, 'articles', articleId);
  const articleDoc = await getDoc(articleRef);
  
  if (!articleDoc.exists()) {
    return [];
  }

  const article = articleDoc.data() as Article;
  
  // Return only the reaction types where the user is in the users array
  return Object.entries(article.reactions || {})
    .filter(([_, value]) => value?.users?.includes(userId))
    .map(([key]) => key as ReactionType);
}; 